import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils/cn";

import { CATALOGUE_TABS, type CatalogueTab } from "../types";
import { DashboardTab } from "./DashboardTab";
import { useCatalogueDialogs } from "./dialogs";
import { ListTab } from "./ListTab";

/**
 * « Contenu du questionnaire » — managing the questionnaire catalogue without
 * the Django admin. The tab is a search param so a tab can be linked to and
 * survives a reload.
 */
export function CatalogueApp() {
  const { t } = useTranslation();
  const search = useSearch({ strict: false }) as { tab?: string };
  const navigate = useNavigate();
  const tab: CatalogueTab = (CATALOGUE_TABS as readonly string[]).includes(search.tab ?? "")
    ? (search.tab as CatalogueTab)
    : "thematics";
  const dialogs = useCatalogueDialogs();

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t("catalogue.eyebrow")}
        </p>
        <h1 className="text-2xl font-bold">{t("catalogue.title")}</h1>
      </header>
      <nav
        role="tablist"
        aria-label={t("catalogue.title")}
        className="flex flex-wrap gap-1 border-b border-border"
      >
        {CATALOGUE_TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() =>
              void navigate({ to: "/catalogue", search: { tab: key } })
            }
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium",
              tab === key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t(`catalogue.tabs.${key}`)}
          </button>
        ))}
      </nav>
      <div role="tabpanel">
        {tab === "issues" || tab === "goals" || tab === "actions" ? (
          <DashboardTab key={tab} tab={tab} dialogs={dialogs} />
        ) : (
          <ListTab tab={tab} dialogs={dialogs} />
        )}
      </div>
      {dialogs.elements}
    </div>
  );
}
